import React, { useEffect, useState } from "react";
import axios from "axios";
import { Search, X, Check, Trash2, ArrowUpDown } from "lucide-react";

const AdminOrder = () => {
  const [orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("https://api.kamulyaan.id/order");
        setOrders(Array.isArray(data) ? data : data?.data || data?.orders || []);
      } catch (err) {
        console.error("Failed to load orders:", err);
        setError("Gagal memuat daftar pesanan");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Filter
  const filtered = orders.filter((o) =>
    [
      o.id,
      o.buyer_id,
      o.store_id,
      o.product_id,
      o.title,
      o.status,
      o.total_amount,
    ].some((v) => String(v ?? "").toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Sort (unchanged)
  const sorted = [...filtered].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    if (sortConfig.key === "total_amount" || sortConfig.key === "quantity") {
      aVal = Number(aVal ?? 0);
      bVal = Number(bVal ?? 0);
    }

    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortIcon = (key) =>
    sortConfig.key === key
      ? sortConfig.direction === "asc" ? "↑" : "↓"
      : <ArrowUpDown size={14} />;

  // Selection
  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAll = () =>
    setSelectedIds(
      selectedIds.size === sorted.length && sorted.length > 0
        ? new Set()
        : new Set(sorted.map((o) => o.id))
    );

  const clearSelection = () => setSelectedIds(new Set());

  // Bulk placeholder
  const handleBulk = (action) => {
    if (!selectedIds.size) return;
    alert(`Bulk ${action} (${selectedIds.size} pesanan) - TODO`);
    clearSelection();
  };

  // Open modal
  const openEditModal = (order) => {
    setEditingOrder({ ...order });
    setSaveMessage("");
    setShowEditModal(true);
  };

  // Save status + send notifications
  const saveStatus = async () => {
    if (!editingOrder) return;

    setSaving(true);
    setSaveMessage("");

    try {
      // 1. Update order status
      await axios.put(`https://api.kamulyaan.id/order/${editingOrder.id}`, {
        status: editingOrder.status,
      });

      // 2. Refresh local orders list
      setOrders((prev) =>
        prev.map((o) =>
          o.id === editingOrder.id ? { ...o, status: editingOrder.status } : o
        )
      );

      // 3. Send notification to buyer & seller
      const notifyMessage = `Status pesanan #${editingOrder.id} telah diperbarui menjadi "${editingOrder.status}". Silakan cek detail di Panel ${editingOrder.buyer_id ? "Pembelian" : "Penjualan"} Anda.`;

      // Notify buyer
      await axios.post("https://api.kamulyaan.id/msg", {
        user_id: editingOrder.buyer_id,
        order_id: editingOrder.id,
        title: "Update Status Pesanan",
        message: notifyMessage,
      });

      // Notify seller
      await axios.post("https://api.kamulyaan.id/msg", {
        user_id: editingOrder.store_id,
        order_id: editingOrder.id,
        title: "Update Status Pesanan",
        message: notifyMessage,
      });

      setSaveMessage("Status diperbarui & notifikasi terkirim ke pembeli & penjual ✓");
      setTimeout(() => setShowEditModal(false), 1800);
    } catch (err) {
      console.error("Save error:", err);
      setSaveMessage("Gagal: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Memuat...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="admin-page p-4 max-w-full">
      <header className="mb-4">
        <h1 className="text-2xl font-bold mb-3">Admin Panel – Kelola Pesanan</h1>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] flex items-center bg-white border border-gray-300 rounded-full px-3 py-2">
            <Search size={18} className="text-gray-500" />
            <input
              className="flex-1 ml-2 outline-none bg-transparent"
              placeholder="Cari ID, produk, buyer, seller, status, total..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>

      {selectedIds.size > 0 && (
        <div className="bg-amber-50 p-3 rounded-lg mb-4 flex flex-wrap gap-3 items-center">
          <span>{selectedIds.size} terpilih</span>
          <button onClick={clearSelection} className="px-4 py-1.5 bg-gray-200 rounded">
            Batal
          </button>
          <button
            onClick={() => handleBulk("update-status")}
            className="px-4 py-1.5 bg-green-500 text-white rounded flex items-center gap-1"
          >
            <Check size={16} /> Update Status
          </button>
          <button
            onClick={() => handleBulk("delete")}
            className="px-4 py-1.5 bg-red-600 text-white rounded flex items-center gap-1"
          >
            <Trash2 size={16} /> Hapus
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white max-h-[70vh]">
        <table className="w-full min-w-[1300px] border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={selectedIds.size === sorted.length && sorted.length > 0}
                  onChange={selectAll}
                />
              </th>
              {[
                { label: "ID", key: "id" },
                { label: "Produk", key: "title" },
                { label: "Buyer ID", key: "buyer_id" },
                { label: "Seller ID", key: "store_id" },
                { label: "Produk ID", key: "product_id" },
                { label: "Jumlah", key: "quantity" },
                { label: "Total", key: "total_amount" },
                { label: "Status", key: "status" },
                { label: "Tanggal", key: "order_date" },
                { label: "Aksi", key: null },
              ].map(({ label, key }) => (
                <th
                  key={label}
                  className={`p-3 text-left font-semibold cursor-pointer whitespace-nowrap hover:bg-gray-200 ${
                    key ? "" : "text-right"
                  }`}
                  onClick={() => key && toggleSort(key)}
                >
                  {label} {key && sortIcon(key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center py-16 text-gray-500">
                  Tidak ada data pesanan
                </td>
              </tr>
            ) : (
              sorted.map((order) => {
                const selected = selectedIds.has(order.id);
                return (
                  <tr key={order.id} className={selected ? "bg-blue-50" : "hover:bg-gray-50"}>
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleSelect(order.id)}
                      />
                    </td>
                    <td className="p-3 font-medium">{order.id}</td>
                    <td className="p-3 max-w-xs truncate text-left font-normal">
                      {order.title || "—"}
                    </td>
                    <td className="p-3">{order.buyer_id || "—"}</td>
                    <td className="p-3">{order.store_id || "—"}</td>
                    <td className="p-3">{order.product_id || "—"}</td>
                    <td className="p-3">{order.quantity || 0}</td>
                    <td className="p-3 font-medium text-green-700 text-right">
                      Rp {Number(order.total_amount ?? 0).toLocaleString("id-ID")}
                    </td>
                    <td className="p-3">
                      <span
                        className={`inline-block px-3 py-1 text-xs rounded-full ${
                          order.status === "Belum bayar"
                            ? "bg-yellow-100 text-yellow-800"
                            : order.status === "Sudah bayar"
                            ? "bg-green-100 text-green-800"
                            : order.status === "Processing"
                            ? "bg-blue-100 text-blue-800"
                            : order.status === "Sudah dikirim"
                            ? "bg-purple-100 text-purple-800"
                            : order.status === "Paket diterima" || order.status === "Selesai"
                            ? "bg-emerald-100 text-emerald-800"
                            : order.status === "Batal"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {order.status || "—"}
                      </span>
                    </td>
                    <td className="p-3">{order.order_date?.split("T")[0] || "—"}</td>
                    <td className="p-3 whitespace-nowrap text-right">
                      <button
                        onClick={() => openEditModal(order)}
                        className="inline-block px-3 py-1.5 bg-blue-600 text-white rounded mr-2 text-sm"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm("Hapus pesanan ini?")) {
                            alert("Hapus pesanan (TODO)");
                          }
                        }}
                        className="inline-block px-3 py-1.5 bg-red-600 text-white rounded text-sm"
                      >
                        ❌
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Edit Status Pesanan #{editingOrder.id}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Status Pesanan
                </label>
                <select
                  value={editingOrder.status || ""}
                  onChange={(e) =>
                    setEditingOrder((prev) => ({ ...prev, status: e.target.value }))
                  }
                  disabled={saving}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none bg-white disabled:opacity-60"
                >
                  <option value="Belum bayar">Belum bayar</option>
                  <option value="Sudah bayar">Sudah bayar</option>
                  <option value="Processing">Processing</option>
                  <option value="Sudah dikirim">Sudah dikirim</option>
                  <option value="Paket diterima">Paket diterima</option>
                  <option value="Selesai">Selesai</option>
                  <option value="Batal">Batal</option>
                </select>
              </div>

              <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg space-y-1">
                <p><strong>Produk:</strong> {editingOrder.title || "—"}</p>
                <p><strong>Total:</strong> Rp {Number(editingOrder.total_amount ?? 0).toLocaleString("id-ID")}</p>
              </div>

              {saveMessage && (
                <p className={`text-center text-sm ${saveMessage.includes("Gagal") ? "text-red-600" : "text-green-600"}`}>
                  {saveMessage}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-4 border-t px-6 py-5">
              <button
                onClick={() => setShowEditModal(false)}
                disabled={saving}
                className="px-6 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
              >
                Batal
              </button>
              <button
                onClick={saveStatus}
                disabled={saving}
                className={`px-6 py-3 rounded-xl text-white font-medium flex items-center gap-2 ${
                  saving ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {saving ? (
                  <>
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                    Menyimpan...
                  </>
                ) : (
                  "Simpan Perubahan"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrder;